import Button, {ButtonGroup} from '@pinecast/common/Button';
import {toMediumDate} from '@pinecast/common/dates';
import {formatCurrency} from '@pinecast/common/helpers/format';
import {InlineSkeleton} from '@pinecast/common/Skeleton';
import {useConfirmBox} from '@pinecast/common/Stage';
import SegmentedStats from '@pinecast/db-spa/SegmentedStats';
import type {QueryResult} from '@pinecast/gql';
import {useManagedMutation} from '@pinecast/gql';
import type {
  CancelSubscription,
  CancelSubscriptionVariables,
} from '@pinecast/gql/__generated_tj__/CancelSubscription';
import type {
  ReactivateSubscription,
  ReactivateSubscriptionVariables,
} from '@pinecast/gql/__generated_tj__/ReactivateSubscription';
import type {RecurringDonation_tipJarSubscription} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import type {RecurringDonationSupplemental} from '@pinecast/gql/__generated_tj__/RecurringDonationSupplemental';
import {FormattedMessage} from '@pinecast/i18n';
import gql from 'graphql-tag';
import * as React from 'react';

const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($slug: ID!) {
    cancelSubscription(podcastSlug: $slug) {
      id
      willCancel
      nextInvoiceDate
    }
  }
`;
const REACTIVATE_SUBSCRIPTION = gql`
  mutation ReactivateSubscription($slug: ID!) {
    reactivateSubscription(podcastSlug: $slug) {
      id
      willCancel
      nextInvoiceDate
    }
  }
`;

type Props = {
  subscription: RecurringDonation_tipJarSubscription;
  supplemental: QueryResult<RecurringDonationSupplemental>;
};

const RecurringDonationHeader = ({subscription, supplemental}: Props) => {
  const cancelMutation = useManagedMutation<
    CancelSubscription,
    CancelSubscriptionVariables
  >(CANCEL_SUBSCRIPTION, {
    variables: {slug: subscription.podcast.slug},
  });
  const reactivateMutation = useManagedMutation<
    ReactivateSubscription,
    ReactivateSubscriptionVariables
  >(REACTIVATE_SUBSCRIPTION, {
    variables: {slug: subscription.podcast.slug},
  });

  const confirm = useConfirmBox();
  const handleCancel = React.useCallback(async () => {
    await confirm(
      <FormattedMessage
        description="Title of the confirmation box when cancelling a podcast subscription"
        defaultMessage="Confirm cancelation"
      />,
      <>
        <FormattedMessage
          description="Copy of the confirmation box when cancelling a podcast subscription"
          defaultMessage="Canceling will stop you from being charged again for this show."
        />
        {subscription.eligibleForPrivateContent &&
          subscription.podcast.hasPrivateEpisodes && (
            <>
              {' '}
              <FormattedMessage
                description="Copy of the confirmation box when cancelling a podcast subscription for shows with eligible private content"
                defaultMessage="You'll lose access to private content for the show at the end of the current billing period."
              />
            </>
          )}
      </>,
    );

    await cancelMutation.action();
  }, [
    cancelMutation,
    confirm,
    subscription.eligibleForPrivateContent,
    subscription.podcast.hasPrivateEpisodes,
  ]);
  const handleReactivate = React.useCallback(async () => {
    await confirm(
      <FormattedMessage
        description="Title of the confirmation box when reactivating a podcast subscription"
        defaultMessage="Renew subscription"
      />,
      <FormattedMessage
        description="Copy of the confirmation box when reactivating a podcast subscription"
        defaultMessage="Renewing your subscription will cause your subscription to begin regularly billing you at the end of the current billing period."
      />,
    );

    await reactivateMutation.action();
  }, [reactivateMutation, confirm]);

  return (
    <>
      <SegmentedStats
        stats={[
          {
            label: (
              <FormattedMessage
                description="Header for a podcast subscription amount"
                defaultMessage="Amount"
              />
            ),
            value: formatCurrency(subscription.amount, subscription.currency),
          },
          {
            label: (
              <FormattedMessage
                description="Header for when a subscription was started"
                defaultMessage="Started"
              />
            ),
            value: toMediumDate(subscription.created),
          },
          !supplemental.error &&
            (supplemental.loading || supplemental.data.tipJarSubscription) && {
              label: (
                <FormattedMessage
                  description="Header for when a subscription will bill next"
                  defaultMessage="Next charge"
                />
              ),
              value: supplemental.loading ? (
                <InlineSkeleton width="150px" />
              ) : supplemental.data.tipJarSubscription!.willCancel ? (
                <FormattedMessage
                  description="Notice when the podcast will not re-bill"
                  defaultMessage="None"
                />
              ) : (
                toMediumDate(
                  supplemental.data.tipJarSubscription!.nextInvoiceDate,
                )
              ),
            },
        ]}
      />
      {supplemental.data && (
        <ButtonGroup style={{marginTop: '12px'}}>
          <></>
          {supplemental.data.tipJarSubscription!.willCancel ? (
            <Button
              onClick={handleReactivate}
              pending={reactivateMutation.pending}
            >
              <FormattedMessage
                description="Button to reactivate a canceled subscription"
                defaultMessage="Set to renew…"
              />
            </Button>
          ) : (
            <>
              <Button onClick={handleCancel} pending={cancelMutation.pending}>
                <FormattedMessage
                  description="Button to cancel an ongoing subscription"
                  defaultMessage="End subscription…"
                />
              </Button>
              <Button href={`/jar/${subscription.podcast.slug}`}>
                <FormattedMessage
                  description="Button to update an ongoing subscription"
                  defaultMessage="Change amount"
                />
              </Button>
            </>
          )}
        </ButtonGroup>
      )}
    </>
  );
};

export default RecurringDonationHeader;
